import { getUploadTemplateURL } from '@/pages/api/scribeAPIV2';
import { constructPublicAssetUrl } from '@/utils/appConfiguration';
import { S3PresignedUrlMethod, TemplateCategory } from '@heidi/schema/ml-scribe';
import { type TemplateResponse } from '@heidi/templates/contracts';
import {
  isJsonFormTemplate,
  isXfaPdfFormTemplate,
  isXmlBackedFormTemplate,
  isXmlFormTemplate,
} from '@heidi/templates/data';

/**
 * S3 key prefixes for form-template PDFs that live in the public
 * template-gallery bucket. Keys with these prefixes are served via public
 * asset URLs; everything else is a user-private template requiring a
 * presigned GET URL.
 */
const PUBLIC_TEMPLATE_PDF_PREFIXES = ['system-template-pdfs/', 'template-gallery-pdfs/'];

export const isPublicTemplatePdfKey = (key: string): boolean =>
  PUBLIC_TEMPLATE_PDF_PREFIXES.some((prefix) => key.startsWith(prefix));

/**
 * Resolves a form template's base PDF URL. Public template-gallery keys use
 * public asset URLs; user-private templates use presigned GET URLs.
 *
 * @throws if the presigned URL response is empty, so callers never fetch('').
 */
export async function resolveFormTemplateUrl(
  objectKey: string,
  templateId?: string
): Promise<string> {
  if (isPublicTemplatePdfKey(objectKey)) {
    return constructPublicAssetUrl(objectKey);
  }

  const { data } = await getUploadTemplateURL({
    object_key: objectKey,
    method: S3PresignedUrlMethod.get_object,
    template_id: templateId,
  });

  if (!data) {
    throw new Error('Failed to get presigned template PDF URL');
  }

  return data;
}

/**
 * Whether a form template's SOURCE document can be downloaded — the base PDF
 * for a PDF form, the XML itself for an XML form.
 *
 * The predicate every list and menu should use: the two formats keep their
 * document in different places (S3 vs `xml_source`), so asking only about
 * `s3_object_key` hides the download on every XML template.
 *
 * Data-driven, not flag-gated: an XML template that already exists must stay
 * downloadable even with the creation flag off.
 */
export const canDownloadFormTemplateSource = (
  template: Pick<TemplateResponse, 'template_category' | 's3_object_key' | 'form_format'>
): boolean => {
  if (template.template_category !== TemplateCategory.FORM_TEMPLATE) return false;
  // Template responses exclude xml_source, so availability is decided by the
  // format alone; the download action fetches the source from
  // GET /templates/{id}/xml-source when it runs.
  if (isXmlFormTemplate(template) || isJsonFormTemplate(template)) return true;
  return Boolean(template.s3_object_key);
};

/**
 * Whether a form template can be opened in the PDF form editor.
 *
 * `false` for XML-backed and JSON forms: the editor is a PDF canvas — it
 * positions field rectangles on rendered pages — and an XML or JSON template
 * has neither a PDF nor meaningful rects. An XFA PDF has a PDF, but its fields
 * live in XML packets, not widgets. All edit in their own modal instead
 * (`useOpenXmlFormEditModal`), which every edit surface routes them to.
 */
export const canEditFormTemplate = (
  template: Pick<TemplateResponse, 'template_category' | 'form_format'>
): boolean => !isXmlBackedFormTemplate(template) && !isJsonFormTemplate(template);

/**
 * Whether a template can be shared by link or email.
 *
 * `false` for XFA PDF forms: the share link carries the source as either a PDF
 * key or XML, and an importer would rebuild an XFA form as a plain PDF form
 * with no fields. Every share entry point hides the action instead.
 */
export const canShareFormTemplate = (
  template: Pick<TemplateResponse, 'template_category' | 'form_format'>
): boolean => !isXfaPdfFormTemplate(template);

/**
 * Whether a form template can be edited AT ALL — in either editor.
 *
 * `canEditFormTemplate` answers "which editor", not "may I": it is
 * `!isXmlBackedFormTemplate`, so `canEditFormTemplate(t) || isXmlBackedFormTemplate(t)` is
 * a tautology and offers Edit for every form template. Ownership is the input
 * that genuinely suppresses it — `is_editable === false` marks a Heidi-authored,
 * community or imported template, which the PDF flow answers by saving a COPY
 * (`useSaveFormTemplate`) and which XML has no copy flow for yet. Keeping the
 * answer here rather than at one caller is what stops a fifth edit surface
 * rediscovering it.
 */
export const canEditFormTemplateAnywhere = (
  template: Pick<TemplateResponse, 'template_category' | 'is_editable'>
): boolean =>
  template.template_category === TemplateCategory.FORM_TEMPLATE &&
  template.is_editable !== false;
